export type AssistantCitation = {
  id: string;
  title: string;
  source_name: string;
  module: string;
  excerpt: string;
  score: number;
};

export type AssistantGrounding = {
  grounded: boolean;
  source: "tools" | "knowledge" | "fallback";
  branch_id?: string | null;
  tool_calls: string[];
  citations: AssistantCitation[];
  references_count?: number;
};

export type AssistantMessage = {
  role: "user" | "assistant";
  content: string;
  grounding?: AssistantGrounding;
  viewModel?: AssistantViewModel;
};
export type AssistantReference = { id: string; title: string; route: string; type: string };
export type AssistantCopilotAction = {
  label: string;
  type: "ask" | "navigate";
  query?: string;
  route?: string;
  tone?: "primary" | "secondary";
  mode?: "chat" | "copilot";
};
export type AssistantPanelMetric = {
  label: string;
  value: string | number;
  tone?: "neutral" | "info" | "warning" | "danger";
};
export type AssistantSelectedItem = {
  type: "processo" | "patente" | "cliente" | "alerta" | "tarefa" | "geral";
  id: string;
  code?: string;
  title: string;
  subtitle?: string;
};

export type AssistantPanelContext = {
  prompt?: string;
  screen?: string;
  entityType?: string;
  entityId?: string;
  intentLabel?: string;
  metrics?: AssistantPanelMetric[];
  alerts?: AssistantPanelMetric[];
  allowedActions?: string[];
  selectedItems?: AssistantSelectedItem[];
};
export type AssistantViewModel = {
  mode: "chat" | "copilot";
  title?: string;
  summary: string;
  findings: string[];
  nextStep?: string;
  actions: AssistantCopilotAction[];
  contextLabel?: string;
};
export type AssistantDonePayload = {
  markdown?: string;
  references?: AssistantReference[];
  grounding?: AssistantGrounding;
  urgency?: Array<Record<string, any>>;
  context?: Record<string, any>;
  mode?: "chat" | "copilot";
  viewModel?: AssistantViewModel;
};

import { API_URL } from "@/services/apiConfig";

function readSse(raw: string) {
  const lines = raw.split("\n").map((line) => line.trimEnd());
  let event = "message";
  let dataRaw = "";
  for (const line of lines) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    if (line.startsWith("data:")) dataRaw += line.slice(5).trim();
  }
  if (!dataRaw) return null;
  try {
    return { event, payload: JSON.parse(dataRaw) };
  } catch {
    return null;
  }
}

function applyRollingWindow(messages: AssistantMessage[], maxMessages = 10, maxChars = 12000): AssistantMessage[] {
  if (!Array.isArray(messages) || messages.length <= 2) return messages || [];
  if (messages.length <= maxMessages) return messages;

  // Always keep first user message (conversation start) + most recent messages
  const first = messages[0];
  const recent = messages.slice(-maxMessages + 1);
  const combined = [first, ...recent];

  let totalChars = 0;
  const pruned: AssistantMessage[] = [];
  for (let i = combined.length - 1; i >= 0; i--) {
    const len = combined[i].content?.length || 0;
    if (totalChars + len > maxChars && pruned.length >= 2) break;
    totalChars += len;
    pruned.unshift(combined[i]);
  }
  return pruned;
}

export const assistantService = {
  streamChat: async (args: {
    messages: AssistantMessage[];
    moduleContext?: string;
    mode?: "chat" | "copilot";
    panelContext?: AssistantPanelContext;
    onChunk: (text: string) => void;
    onMetadata: (references: AssistantReference[], grounding?: AssistantGrounding) => void;
    onDone: (payload: AssistantDonePayload) => void;
    onError: (error: string) => void;
  }) => {
    const token = localStorage.getItem("token");
    const prunedMessages = applyRollingWindow(args.messages);
    try {
      const response = await fetch(`${API_URL}/assistant/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          moduleContext: args.moduleContext || "geral",
          mode: args.mode || "chat",
          panelContext: args.panelContext,
          messages: prunedMessages,
        }),
      });

      if (!response.ok || !response.body) {
        // Lê o corpo do erro (o backend devolve { error }) para reportar a
        // causa REAL — antes o status era descartado e qualquer falha (403 de
        // permissão incluído) virava "servidor não respondeu".
        let detail = "";
        try {
          const errText = await response.text();
          try {
            const parsed = JSON.parse(errText);
            detail = String(parsed?.error || parsed?.message || "");
          } catch {
            detail = errText.slice(0, 140);
          }
        } catch { /* corpo vazio */ }
        const err = new Error(`Erro ${response.status}${detail ? `: ${detail}` : ""}`) as any;
        err.status = response.status;
        throw err;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let partial = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let boundary = buffer.indexOf("\n\n");
        while (boundary !== -1) {
          const block = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const parsed = readSse(block);
          if (parsed?.event === "chunk") {
            partial += String(parsed.payload?.text || "");
            args.onChunk(partial);
          }
          if (parsed?.event === "metadata") {
            args.onMetadata(
              Array.isArray(parsed.payload?.references) ? parsed.payload.references : [],
              parsed.payload?.grounding
            );
          }
          if (parsed?.event === "done") args.onDone(parsed.payload || {});
          boundary = buffer.indexOf("\n\n");
        }
      }

      if (buffer.trim()) {
        const parsed = readSse(buffer.trim());
        if (parsed?.event === "done") args.onDone(parsed.payload || {});
      }
    } catch (err: any) {
      // Reporte honesto: distingue rede/servidor-caído de erros HTTP reais.
      // Antes, QUALQUER falha (401/403/429/5xx) era traduzida para "o servidor
      // não respondeu" — o 403 de permissão aparecia como "IA offline".
      const status = Number(err?.status || 0);
      const detail = typeof err?.message === "string" && err.message.includes(": ")
        ? err.message.split(": ").slice(1).join(": ").trim()
        : "";
      let message: string;
      if (!status) {
        message = "⚠️ **IA indisponível** — não foi possível conectar ao servidor. Verifique sua conexão e se o backend está rodando.";
      } else if (status === 401) {
        message = "🔐 **Sessão expirada** — faça login novamente para continuar usando o copiloto.";
      } else if (status === 403) {
        message = `🚫 **Acesso negado ao copiloto**${detail ? ` — ${detail}` : ""}. Peça ao administrador da sua empresa para liberar o acesso.`;
      } else if (status === 429) {
        message = "⏳ **Limite de uso atingido** — aguarde alguns instantes e tente novamente.";
      } else if (status >= 500) {
        message = `⚠️ **Erro no servidor (${status})** — o backend respondeu, mas falhou ao processar a conversa. Tente novamente em instantes.`;
      } else {
        message = `⚠️ **Falha na conversa (${status})**${detail ? `: ${detail}` : ""}.`;
      }
      args.onChunk(message);
      args.onMetadata([], undefined);
      args.onDone({ markdown: message });
      args.onError(err?.message || message);
    }
  },
};
